import { Governance } from "@solana/spl-governance";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import {
  WalletModalProvider,
  WalletMultiButton,
} from "@solana/wallet-adapter-react-ui";
import { PublicKey } from "@solana/web3.js";
import { pow } from "mathjs";
import { FC, useEffect, useState } from "react";

import arrowRight from "../../assets/arrowRightBlack.svg";
import {
  localeStringOptions,
  MESSAGE_TYPE,
} from "../../common/constants/common.constants";
import {
  OfferStatus,
  ProposalAction,
} from "../../common/enums/proposals.enums";
import { IDetailNftData } from "../../common/interfaces/common.interface";
import {
  IBasicProposalData,
  IOffer,
} from "../../common/interfaces/proposals.interface";
import {
  getMintMetadata,
  parseMetadataToNft,
} from "../../solana/methods/general";
import { acceptProposalsOffer } from "../../solana/methods/proposals";
import { getTrimmedPublicKey } from "../../utilities/helpers";
import { createNotification } from "../../utilities/notification";
import Fees from "../Fees/Fees";
import SubmitButton from "../SubmitButton/SubmitButton";
import "./BuySellNft.scss";

const BuySellNft: FC<{
  offer: IOffer;
  clubName: string;
  proposal: IBasicProposalData;
  governance: Governance;
  decimals: number;
  symbol: string;
}> = ({ offer, clubName, proposal, governance, decimals, symbol }) => {
  const [nft, setNft] = useState<IDetailNftData>();
  const wallet = useAnchorWallet();
  const [offerStatus, setOfferStatus] = useState(offer.status);
  const [acceptingOffer, toggleAcceptingOffer] = useState(false);

  useEffect(() => {
    void getNft(
      offer.type === ProposalAction.BuyNft
        ? offer.wantedTokenMint
        : offer.offeredTokenMint
    );
  }, []);

  const getNft = async (nftMintAddress: PublicKey) => {
    const metadata = await getMintMetadata(nftMintAddress);
    const nft: IDetailNftData = parseMetadataToNft(
      metadata,
      offer.type === ProposalAction.BuyNft
        ? offer.makerWantedTokenAccount
        : offer.makerOfferedTokenAccount,
      nftMintAddress
    );
    setNft(nft);
  };

  const acceptOffer = async () => {
    try {
      toggleAcceptingOffer(true);

      await acceptProposalsOffer(
        offer,
        wallet!,
        clubName,
        governance,
        proposal,
        offer.type
      );
      setOfferStatus(OfferStatus.accepted);
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        `Offer accepting failed`,
        error.message
      );
    } finally {
      toggleAcceptingOffer(false);
    }
  };

  const isOfferDedicatedForWallet =
    (wallet &&
      offer.dedicatedTaker &&
      offer.dedicatedTaker.toString() === wallet!.publicKey.toString()) ||
    !offer.dedicatedTaker;

  return (
    <WalletModalProvider>
      <div className="buy-sell-nft">
        <p className="buy-sell-nft__club-name">{clubName}</p>
        <p className="buy-sell-nft__type">
          {offer.type === ProposalAction.BuyNft
            ? "wants to buy"
            : "wants to sell"}
        </p>
        <div className="buy-sell-nft__nft">
          <img src={nft?.nftUrl} alt="nft_img" />
          <p className="buy-sell-nft__name">{nft?.name}</p>
          <p className="buy-sell-nft__price">
            {offer.type === ProposalAction.BuyNft
              ? (
                  offer.offeredAmount / Number(pow(10, decimals))
                ).toLocaleString(undefined, localeStringOptions)
              : (offer.wantedAmount / Number(pow(10, decimals))).toLocaleString(
                  undefined,
                  localeStringOptions
                )}{" "}
            {symbol}
          </p>
        </div>
        {wallet ? (
          offerStatus === OfferStatus.initialised ? (
            isOfferDedicatedForWallet ? (
              <SubmitButton
                onClick={acceptOffer}
                className="buy-sell-nft__button"
                submitting={acceptingOffer}
                disabled={acceptingOffer}
              >
                {offer.type === ProposalAction.BuyNft
                  ? "Sell this NFT"
                  : "Buy this NFT"}
                <img src={arrowRight} alt="" />
              </SubmitButton>
            ) : (
              offer.dedicatedTaker && (
                <Fees
                  feesDescription={`This offer is reserved for ${getTrimmedPublicKey(
                    offer.dedicatedTaker?.toString()
                  )}`}
                  feesTitle="Offer reserved"
                  buySell
                />
              )
            )
          ) : (
            <Fees feesTitle="Sales are closed" buySell={true} />
          )
        ) : (
          <WalletMultiButton className="buy-sell-nft__wallet-button" />
        )}
      </div>
    </WalletModalProvider>
  );
};

export default BuySellNft;
